import * as CONSTANTS from "./constants";

function roleGrouping(it = null, soc = null, scada = null, pio = null) {
    if (it === null) {
        it  = [];
    }
    if (soc === null) {
        soc = [];
    }
    if (scada === null) {
        scada = [];
    }
    if (pio === null) {
        pio = [];
    }
    return {
        it: it,
        soc: soc,
        scada: scada,
        pio: pio
    };
}

export class NodeData {
    name;
    /** node type */
    type;
    group;
    /** internal status of node health; the visibility of the true status by role depends on self.attack_visibility */
    status;
    /** has a role used an action to investigate this role to see if it has an attack or not */
    investigated;
    /** array of strings of the roles who can see an unknown status and notifications if 
     * this machine is either a red herring or infected */
    attackVisibility;
    /** TODO use all device cost values from tsv and update loss calculation function
     * engineering only cost for shutting off / restarting the node */
    costShutoff;
    /** engineering only cost for removing the virus, new hardware, and restoring the system to its previous state */
    costInfected;
    /** a basis for the disruption cost to the community; if the node is left infected, it multiplies */
    costGoodwill;
    latitude;
    longitude;
    statusHistory = [];
    notifications = [];
    description;
    caught;
    
    constructor() {}

    /**
     * Factory method for building a new NodeData
     * @param {string} name
     * @param {string} group
     * @param {string} status
     * @param {bool} investigated
     * @param {array} attack_visibility
     * @param {number} lat latitude
     * @param {number} long longitude
     * @param {number} costShutoff
     * @param {number} costInfected
     * @param {number} costGoodwill
     * @param {string} nodeType
     * @param {string} description = null
     * @param notifications = null
     * @return {NodeData} instance
     */
    static build(name, group, status, investigated, attackVisibility, latitude, longitude, costShutoff, costInfected, costGoodwill, type, description = "", notifications = null) {
        let instance = new NodeData();
        instance.name = name;
        instance.description = description;
        instance.type = type;
        instance.group = group;
        instance.status = status;
        instance.investigated = investigated;
        instance.attackVisibility = attackVisibility;
        instance.costShutoff = costShutoff;
        instance.costInfected = costInfected;
        instance.costGoodwill = costGoodwill;
        instance.latitude = latitude;
        instance.longitude = longitude;
        instance.statusHistory = [];
        instance.statusHistory["1"] = status;
        instance.notifications = (notifications === null || notifications === [] ? roleGrouping() : notifications);
        instance.caught = false;
        return instance;
    }

    /**
     * return true if node is infected (INFECTED_DIRECTED or INFECTED_SPREAD)
     * @return {bool}
     */
    isInfected() {
        return this.status === CONSTANTS.INFECTED_DIRECTED || this.status === CONSTANTS.INFECTED_SPREAD;
    }

    /**
     * return true if this.status is OFF
     * @return {bool}
     */
    isOff() {
        return this.status === CONSTANTS.OFF;
    }

    /**
     * return true if node is healthy
     * @return {bool}
     */
    isHealthy() {
        return this.status === CONSTANTS.HEALTHY;
    }
    
    /**
     * update node status and log status history in this.statusHistory
     * @param attack_visibility array of who can see the attack
     * @param {string} new_status
     * @param {number} round
     */
    updateStatus(attackVisibility, newStatus, round) {
        this.attackVisibility = attackVisibility;
        this.status = newStatus;
        if (this.statusHistory.includes(round.toString())) {
            this.statusHistory[round.toString()].push(newStatus);
        }
        else {
            this.statusHistory[round.toString()] = [newStatus];
        }
    }

    /** gets the status of the node from the perspective of one role. Only roles in self.attack_visibility can see an
     * 'unknown' external status is the internal status is red_herring or infected_*, and the true internal status is
     * only revealed after the node has been investigated. 
     * @param {string} role
     * @param {bool} gameInProgress
     */
    getVisibleStatus(role, gameInProgress) {
        // when the game is over, return true statuses to all roles
        if (!gameInProgress) return this.isInfected() ? this.getPublicStatus() : this.status;

        if (this.status === CONSTANTS.OFF) return this.status;

        if (this.investigated) return this.getPublicStatus();

        if (this.attackVisibility.includes(role)) return CONSTANTS.UNKNOWN;

        if (this.isInfected()) return CONSTANTS.UNKNOWN;

        // this check must go after the unknown check since red herrings do not have statuses, but instead rely on having
        // at least 1 role in self.attack_visibility
        if (this.status === CONSTANTS.HEALTHY) return CONSTANTS.HEALTHY;

        throw new Error(`read unknown status ${this.status} for node ${this.name}`);
    }

    /**
     * get unhidden status (COMPROMISED|HEALTHY|OFF|etc.)
     * @return {string} status
     */
    getPublicStatus() {
        return this.isInfected() ? CONSTANTS.COMPROMISED : this.status;
    }

    /**
     * This prepares the Node to be passed directly to the client by setting its status to its visible status
     */
    prepForClient(role, gameInProgress) {
        this.status = this.getVisibleStatus(role, gameInProgress);
    }

    toJSON() {
        return {
            name: this.name,
            type: this.type,
            group: this.group,
            status: this.status,
            investigated: this.investigated,
            attack_visibility: this.attackVisibility,
            cost_shutoff: this.costShutoff,
            cost_infected: this.costInfected,
            cost_goodwill: this.costGoodwill,
            latitude: this.latitude,
            longitude: this.longitude,
            status_history: this.statusHistory,
            notifications: this.notifications,
            description: this.description,
            caught: this.caught
        };
    }

    static jsonDeserialize(jsonString) {
        let instance = new NodeData();
        let data = jsonString;
        instance.name = data.name;
        instance.type = data.type;
        instance.group = data.group;
        instance.status = data.status;
        instance.investigated = data.investigated;
        instance.attackVisibility = data.attack_visibility;
        instance.costShutoff = data.cost_shutoff;
        instance.costInfected = data.cost_infected;
        instance.costGoodwill = data.cost_goodwill;
        instance.latitude = data.latitude;
        instance.longitude = data.longitude;
        instance.statusHistory = data.status_history;
        instance.notifications = data.notifications;
        instance.description = data.description;
        if ("caught" in data) {
            instance.caught = data.caught;
        }
        else {
            instance.caught = false;
        }
        return instance;
    }

    addNotifications(newNotifications, role) {
        this.notifications[role] = [...this.notifications[role], ...newNotifications[role]];
    }

    /**
     * push a new role onto the attack visibility array
     * @param {string} role
     */
    pushAttackVisibility(role) {
        this.attackVisibility.push(role);
    }

    /**
     * set @var {bool} investigated to true
     */
    investigate() {
        this.investigated = true;
    }

    catchNode() {
        this.caught = true;
    }

    getName() {
        return this.name;
    }

    /**
     * Get the value of costShutoff
     */ 
    getCostShutoff() {
        return this.costShutoff;
    }

    getCostInfected() {
        return this.costInfected;
    }

    getCostGoodwill() {
        return this.costGoodwill;
    }

    getStatus() {
        return this.status;
    }

    getAttackVisibility() {
        return this.attackVisibility;
    }

    getGroup() {
        return this.group;
    }

    getType() {
        return this.type;
    }

    isInvestigated() {
        return this.investigated;
    }

    getNotifications() {
        return this.notifications;
    }

    isCaught() {
        return this.caught;
    }
}
